// Server-backed likes + ejects via Supabase. Each row is either a 'love'
// or an 'eject' — mutually exclusive per (user, tmdb_id, media_type).
//
// Loves feed POSITIVE signal into the recommendation algorithm.
// Ejects feed NEGATIVE signal (the algorithm steers AWAY from things like
// what you ejected) AND filter the ejected title itself out of results.
import { supabase, supabaseConfigured } from './supabase.js';

let currentUserId = null;
const currentLoves  = new Set();  // "media:id"
const currentEjects = new Set();  // "media:id"
let initialized   = false;

const subscribers = new Set();
function notify() {
  const loves  = [...currentLoves].map(parseKey);
  const ejects = [...currentEjects].map(parseKey);
  for (const cb of subscribers) cb({ loves, ejects });
}

function key(tmdbId, mediaType) {
  return `${mediaType === 'tv' ? 'tv' : 'movie'}:${tmdbId}`;
}
function parseKey(k) {
  const [media, id] = k.split(':');
  return { media_type: media, tmdb_id: Number(id) };
}

export function subscribeLikes(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

export async function initLikesForUser(userId) {
  if (!supabaseConfigured) { initialized = true; return; }
  currentUserId = userId || null;
  currentLoves.clear();
  currentEjects.clear();
  if (!userId) { initialized = true; notify(); return; }

  const { data } = await supabase
    .from('likes')
    .select('tmdb_id, media_type, kind')
    .eq('user_id', userId);
  for (const r of data || []) {
    const k = key(r.tmdb_id, r.media_type || 'movie');
    if ((r.kind || 'love') === 'eject') currentEjects.add(k);
    else currentLoves.add(k);
  }
  initialized = true;
  notify();
}

export function getLoves() {
  return [...currentLoves].map(parseKey);
}
export function getEjects() {
  return [...currentEjects].map(parseKey);
}
// Backward-compat: getLikes returns loves (used by some old code paths).
export function getLikes() { return getLoves(); }

export function isLoved(tmdbId, mediaType = 'movie') {
  return currentLoves.has(key(tmdbId, mediaType));
}
export function isEjected(tmdbId, mediaType = 'movie') {
  return currentEjects.has(key(tmdbId, mediaType));
}
// Backward-compat:
export function isLiked(tmdbId, mediaType = 'movie') {
  return isLoved(tmdbId, mediaType);
}

export function isInitialized() {
  return initialized;
}

// Toggle love. If currently ejected, flips to loved (deletes the eject row
// and inserts a love row in one upsert).
export async function toggleLove(tmdbId, mediaType = 'movie') {
  return await setKind(tmdbId, mediaType, isLoved(tmdbId, mediaType) ? null : 'love');
}

// Toggle eject. Same logic — flipping from loved is allowed.
export async function toggleEject(tmdbId, mediaType = 'movie') {
  return await setKind(tmdbId, mediaType, isEjected(tmdbId, mediaType) ? null : 'eject');
}

// Backward-compat for code still calling toggleLike (treated as toggleLove).
export const toggleLike = toggleLove;

// Internal: sets the kind for a (id, type) to 'love' / 'eject' / null
// (delete). Optimistically updates the in-memory sets then persists.
async function setKind(tmdbId, mediaType, kind) {
  if (!supabaseConfigured || !currentUserId) {
    throw new Error('Sign in to save loves.');
  }
  const id = Number(tmdbId);
  const mt = mediaType === 'tv' ? 'tv' : 'movie';
  const k  = key(id, mt);

  // Optimistic local update
  const wasLoved  = currentLoves.has(k);
  const wasEjected = currentEjects.has(k);
  currentLoves.delete(k);
  currentEjects.delete(k);
  if (kind === 'love')  currentLoves.add(k);
  if (kind === 'eject') currentEjects.add(k);
  notify();

  try {
    if (kind === null) {
      const { error } = await supabase
        .from('likes')
        .delete()
        .eq('user_id', currentUserId)
        .eq('tmdb_id', id)
        .eq('media_type', mt);
      if (error) throw error;
    } else {
      // Upsert into the composite primary key (user_id, tmdb_id, media_type).
      const { error } = await supabase
        .from('likes')
        .upsert(
          { user_id: currentUserId, tmdb_id: id, media_type: mt, kind },
          { onConflict: 'user_id,tmdb_id,media_type' }
        );
      if (error) throw error;
    }
    return kind;
  } catch (e) {
    // Roll back optimistic update.
    currentLoves.delete(k);
    currentEjects.delete(k);
    if (wasLoved)   currentLoves.add(k);
    if (wasEjected) currentEjects.add(k);
    notify();
    throw e;
  }
}

// Fetch another user's loves (used for /u/:slug pages — viewers see what
// the profile owner has hearted, ejects stay private).
export async function fetchLikesForUserId(userId) {
  if (!supabaseConfigured || !userId) return [];
  const { data } = await supabase
    .from('likes')
    .select('tmdb_id, media_type, created_at')
    .eq('user_id', userId)
    .eq('kind', 'love')
    .order('created_at', { ascending: false });
  return data || [];
}
